/**
 * MiCartera — vista personal del vendedor con su cartera vencida y por vencer,
 * más inventario próximo a caducar. Incluye descargas PDF y envío semanal automático.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileDown, AlertCircle, Clock, Package, Mail, RefreshCw, Send, Handshake, Plus, CheckCircle2, XCircle, Edit3, Download } from "lucide-react";
import { Link } from "react-router-dom";

function downloadPDF(path, filename) {
  // ITER149: usar el cliente api (con Authorization Bearer) en vez de fetch crudo
  return api.get(path, { responseType: "blob" })
    .then(r => {
      const blob = r.data;
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(u);
    })
    .catch((err) => { console.error("downloadPDF failed:", err); toast.error("Error al generar PDF"); });
}

export default function MiCartera() {
  return (
    <div className="space-y-6" data-testid="mi-cartera-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Cartera</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Mi cartera</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Facturas vencidas, próximas a vencer e inventario por caducar. Recibes un resumen cada lunes 8:00 AM por correo.</p>
      </header>
      <Tabs defaultValue="cartera">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="cartera" data-testid="tab-cartera"><AlertCircle size={14} className="mr-2" />Cartera</TabsTrigger>
          <TabsTrigger value="compromisos" data-testid="tab-compromisos"><Handshake size={14} className="mr-2" />Compromisos de pago</TabsTrigger>
          <TabsTrigger value="inventario" data-testid="tab-inventario"><Package size={14} className="mr-2" />Inventario por caducar</TabsTrigger>
        </TabsList>
        <TabsContent value="cartera"><CarteraTab /></TabsContent>
        <TabsContent value="compromisos"><CompromisosTab /></TabsContent>
        <TabsContent value="inventario"><InventarioTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function CarteraTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enviarOpen, setEnviarOpen] = useState(null); // {cliente_id, cliente_nombre, email}
  const [vendedoresList, setVendedoresList] = useState([]);
  const [selectedVendedor, setSelectedVendedor] = useState(""); // vacío = propio
  const load = (vid = selectedVendedor) => {
    setLoading(true);
    const url = vid ? `/vendor-cartera/mi-cartera?vendedor_id=${vid}` : "/vendor-cartera/mi-cartera";
    api.get(url)
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(""); /* primera carga = propio */ // eslint-disable-next-line
  }, []);
  // Cargar lista de vendedores disponibles UNA vez (si soy admin/contab/gerencia, viene el listado completo)
  useEffect(() => {
    api.get("/vendor-cartera/cartera/vendedores-disponibles")
      .then(r => setVendedoresList(r.data || []))
      .catch(() => {});
  }, []);

  if (loading) return <div className="mt-4 text-[#8ba397] text-sm">Cargando cartera...</div>;
  if (!data) return null;

  // Agrupar por cliente para vista personalizada
  const agrupar = (items) => {
    const map = {};
    items.forEach(f => {
      const k = f.cliente_id || "_";
      map[k] = map[k] || { cliente_id: f.cliente_id, cliente_nombre: f.cliente_nombre, cliente_nombre_comercial: f.cliente_nombre_comercial, cliente_email: f.cliente_email, items: [] };
      map[k].items.push(f);
    });
    return Object.values(map).map(g => ({ ...g, items: g.items.sort((a, b) => String(a.numero_factura || "").localeCompare(String(b.numero_factura || ""))), subtotal: g.items.reduce((s, x) => s + x.saldo, 0) })).sort((a, b) => (a.cliente_nombre || "").localeCompare(b.cliente_nombre || ""));
  };

  const t = data.totales || {};
  const pdfQs = selectedVendedor ? `?vendedor_id=${selectedVendedor}` : "";
  return (
    <div className="mt-4 space-y-4">
      {/* Selector de vendedor (solo visible si tengo permiso) */}
      {data.puede_ver_otros && vendedoresList.length > 1 && (
        <div className="rounded-md border border-[#d1dbd6] bg-white p-3 flex flex-wrap items-end gap-3" data-testid="vendedor-selector-wrap">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-xs uppercase tracking-wider text-[#5c7368]">Ver cartera de vendedor</Label>
            <select
              value={selectedVendedor}
              onChange={e => { setSelectedVendedor(e.target.value); load(e.target.value); }}
              className="mt-1 w-full border border-[#d1dbd6] rounded h-10 px-3 text-sm bg-white"
              data-testid="vendedor-selector"
            >
              <option value="">📂 Mi cartera personal</option>
              {vendedoresList.map(v => (
                <option key={v.id} value={v.id}>
                  {v.nombre} {v.role ? `· ${v.role}` : ""}
                </option>
              ))}
            </select>
          </div>
          {!data.es_propia && (
            <Badge className="bg-[#e9f2ee] text-[#205c45] border-[#9ec4b1] border self-center">
              Viendo cartera de <strong className="ml-1">{data.vendedor_nombre}</strong>
            </Badge>
          )}
        </div>
      )}

      {/* Resumen 4 categorías */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="rounded-md border border-[#d99584] bg-[#fdecea] p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#7a2514]">Vencido</div>
          <div className="text-2xl font-bold tabular-nums text-[#7a2514]">${(t.total_vencido || 0).toLocaleString("es-EC", { minimumFractionDigits: 2 })}</div>
          <div className="text-[10px] text-[#7a2514]">{t.count_vencidas || 0} factura{(t.count_vencidas || 0) === 1 ? "" : "s"}</div>
        </div>
        <div className="rounded-md border border-[#f5c477] bg-[#fef7ec] p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#8a5500]">Por vencer ≤15d</div>
          <div className="text-2xl font-bold tabular-nums text-[#8a5500]">${(t.total_proximo || 0).toLocaleString("es-EC", { minimumFractionDigits: 2 })}</div>
          <div className="text-[10px] text-[#8a5500]">{t.count_proximas || 0} factura{(t.count_proximas || 0) === 1 ? "" : "s"}</div>
        </div>
        <div className="rounded-md border border-[#9ec4b1] bg-[#e9f2ee] p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#205c45]">Vigente (&gt;15d)</div>
          <div className="text-2xl font-bold tabular-nums text-[#205c45]">${(t.total_por_vencer || 0).toLocaleString("es-EC", { minimumFractionDigits: 2 })}</div>
          <div className="text-[10px] text-[#205c45]">{t.count_por_vencer || 0} factura{(t.count_por_vencer || 0) === 1 ? "" : "s"}</div>
        </div>
        <div className="rounded-md border border-[#15241d] bg-[#15241d] text-white p-4 flex flex-col justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold opacity-80">TOTAL PENDIENTE</div>
            <div className="text-2xl font-bold tabular-nums">${(t.total_general || 0).toLocaleString("es-EC", { minimumFractionDigits: 2 })}</div>
            <div className="text-[10px] opacity-80">{t.count_total || 0} factura{(t.count_total || 0) === 1 ? "" : "s"}</div>
          </div>
          <div className="flex gap-1 mt-2">
            <Button size="sm" variant="outline" onClick={() => load(selectedVendedor)} className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"><RefreshCw size={12} className="mr-1" />Actualizar</Button>
            <Button onClick={() => downloadPDF(`/vendor-cartera/mi-cartera/pdf${pdfQs}`, `cartera-${data.vendedor_nombre.replace(/\s/g,"_")}-${new Date().toISOString().slice(0,10)}.pdf`)} className="bg-[#205c45] hover:bg-[#1a4a37] flex-1" data-testid="cartera-pdf-btn">
              <FileDown size={14} className="mr-1" />PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Feb 2026: TODAS las facturas pendientes agrupadas por cliente (sin duplicar) */}
      <SeccionCliente
        titulo={`Facturas pendientes por cliente (${(data.todas || []).length})`}
        grupos={agrupar(data.todas || [])}
        color="green"
        onEnviar={setEnviarOpen}
        onDescargar={(g) => downloadPDF(
          `/vendor-cartera/clientes/${g.cliente_id}/estado-cuenta-pdf`,
          `estado_cuenta_${(g.cliente_nombre || 'cliente').replace(/\s/g, '_')}_${new Date().toISOString().slice(0,10)}.pdf`
        )}
      />

      <EnviarEstadoCuentaDialog data={enviarOpen} onClose={() => setEnviarOpen(null)} />
    </div>
  );
}

function SeccionCliente({ titulo, grupos, color, onEnviar, onDescargar }) {
  const headerBg = color === "red" ? "bg-[#fdecea]" : color === "green" ? "bg-[#e9f2ee]" : "bg-[#fef7ec]";
  const headerTxt = color === "red" ? "text-[#7a2514]" : color === "green" ? "text-[#205c45]" : "text-[#8a5500]";
  const totalGen = grupos.reduce((s, g) => s + g.subtotal, 0);
  if (grupos.length === 0) {
    return (
      <div className="bg-white border border-[#d1dbd6] rounded-md overflow-hidden">
        <div className={`px-4 py-2 ${headerBg} text-xs uppercase tracking-wider ${headerTxt} font-semibold border-b border-[#d1dbd6]`}>
          <Clock size={12} className="inline mr-1" />{titulo}
        </div>
        <div className="text-center py-6 text-[#1f6b42] text-xs">Sin facturas en esta categoría ✓</div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#d1dbd6] rounded-md overflow-hidden">
      <div className={`px-4 py-2 ${headerBg} text-xs uppercase tracking-wider ${headerTxt} font-semibold border-b border-[#d1dbd6] flex items-center justify-between`}>
        <span><Clock size={12} className="inline mr-1" />{titulo}</span>
        <span>{grupos.length} cliente{grupos.length === 1 ? "" : "s"} · ${totalGen.toLocaleString("es-EC", { minimumFractionDigits: 2 })}</span>
      </div>
      {grupos.map((g, gi) => (
        <div key={g.cliente_id || gi} className="border-b border-[#e9eceb] last:border-0">
          <div className="px-4 py-2 bg-[#205c45] text-white flex items-center justify-between text-xs">
            <div>
              <span className="font-bold">{g.cliente_nombre}</span>
              {g.cliente_nombre_comercial && g.cliente_nombre_comercial !== g.cliente_nombre && (
                <span className="ml-2 opacity-80 italic">— {g.cliente_nombre_comercial}</span>
              )}
              <span className="ml-2 opacity-80">({g.items.length} factura{g.items.length === 1 ? "" : "s"})</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold tabular-nums">${g.subtotal.toLocaleString("es-EC", { minimumFractionDigits: 2 })}</span>
              {onDescargar && (
                <Button
                  size="sm"
                  onClick={() => onDescargar(g)}
                  className="bg-white/10 text-white border border-white/40 hover:bg-white/20 h-7 text-xs"
                  title="Descargar PDF del estado de cuenta (para compartir por WhatsApp, imprimir, etc.)"
                  data-testid={`download-cliente-${g.cliente_id}`}
                >
                  <Download size={11} className="mr-1" />Descargar
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => onEnviar({ cliente_id: g.cliente_id, cliente_nombre: g.cliente_nombre, email: g.cliente_email })}
                className="bg-white text-[#205c45] hover:bg-[#e9f2ee] h-7 text-xs"
                data-testid={`send-cliente-${g.cliente_id}`}
              >
                <Send size={11} className="mr-1" />Enviar al cliente
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="erp-table text-xs">
              <thead>
                <tr><th>Factura</th><th>Emitida</th><th>Vencimiento</th><th className="text-center">Días</th><th>Estado</th><th className="text-right">Total</th><th className="text-right">Pagado</th><th className="text-right" title="Retención IR + IVA recibida">Retención</th><th className="text-right">Saldo</th><th className="w-14"></th></tr>
              </thead>
              <tbody>
                {g.items.map(f => {
                  const cat = f.categoria;
                  const catBadge = cat === "vencida"
                    ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-[#fdecea] text-[#7a2514] border border-[#d99584]">⚠ Vencida</span>
                    : cat === "proxima"
                    ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]">⏳ ≤15d</span>
                    : cat === "por_vencer"
                    ? <span className="px-1.5 py-0.5 rounded text-[10px] bg-[#e9f2ee] text-[#205c45] border border-[#9ec4b1]">✓ Vigente</span>
                    : <span className="px-1.5 py-0.5 rounded text-[10px] bg-[#f5f8f6] text-[#8ba397] border border-[#d1dbd6]">Sin fecha</span>;
                  return (
                    <tr key={f.id}>
                      <td className="font-mono">{f.numero_factura || "—"}</td>
                      <td>{f.fecha_factura || "—"}</td>
                      <td>{f.fecha_vencimiento || "—"}</td>
                      <td className={`text-center font-semibold tabular-nums ${f.dias < 0 ? "text-[#7a2514]" : f.dias <= 15 ? "text-[#8a5500]" : "text-[#5c7368]"}`}>{f.fecha_vencimiento ? f.dias : "—"}</td>
                      <td>{catBadge}</td>
                      <td className="text-right tabular-nums">${(f.monto_total || 0).toLocaleString("es-EC", { minimumFractionDigits: 2 })}</td>
                      <td className="text-right tabular-nums text-[#5c7368]">${(f.monto_pagado || 0).toLocaleString("es-EC", { minimumFractionDigits: 2 })}</td>
                      <td className={`text-right tabular-nums ${(f.retencion_aplicada || 0) > 0 ? "text-[#205c45] font-semibold" : "text-[#8ba397]"}`}>
                        {(f.retencion_aplicada || 0) > 0 ? `$${(+f.retencion_aplicada).toLocaleString("es-EC", { minimumFractionDigits: 2 })}` : "—"}
                      </td>
                      <td className="text-right font-semibold tabular-nums">${f.saldo.toLocaleString("es-EC", { minimumFractionDigits: 2 })}</td>
                      <td>{f.factura_venta_id && <Link to={`/facturacion?f=${f.factura_venta_id}`}><Button size="sm" variant="outline" title="Ver factura">→</Button></Link>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function EnviarEstadoCuentaDialog({ data, onClose }) {
  const [emailDestino, setEmailDestino] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [enviando, setEnviando] = useState(false);
  useEffect(() => {
    if (data) { setEmailDestino(data.email || ""); setMensaje(""); }
  }, [data]);
  if (!data) return null;
  const enviar = async () => {
    if (!emailDestino) return toast.error("Indique un email destino");
    setEnviando(true);
    try {
      const { data: r } = await api.post(`/vendor-cartera/clientes/${data.cliente_id}/enviar-estado-cuenta`, {
        email_destino: emailDestino,
        mensaje_personalizado: mensaje,
      });
      toast.success(`Enviado a ${r.destino} (saldo $${r.saldo})`);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEnviando(false); }
  };
  return (
    <Dialog open={!!data} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="enviar-cliente-dialog">
        <DialogHeader><DialogTitle>Enviar estado de cuenta — {data.cliente_nombre}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label>Email destino</Label><Input type="email" value={emailDestino} onChange={(e) => setEmailDestino(e.target.value)} placeholder="cliente@empresa.com" data-testid="enviar-cliente-email" /></div>
          <div><Label>Mensaje personalizado (opcional)</Label><Textarea value={mensaje} onChange={(e) => setMensaje(e.target.value)} rows={3} placeholder="Ej: Estimado cliente, le adjuntamos su estado de cuenta para su revisión..." data-testid="enviar-cliente-mensaje" /></div>
          <div className="text-[11px] text-[#5c7368] italic">Se enviará un correo con el PDF del estado de cuenta. La respuesta del cliente llegará a tu correo personal.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={enviar} disabled={enviando} className="bg-[#205c45]" data-testid="enviar-cliente-confirm">
            <Send size={14} className="mr-1" />{enviando ? "Enviando..." : "Enviar correo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InventarioTab() {
  const [dias, setDias] = useState(90);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/vendor-cartera/mi-inventario-caducar?dias=${dias}`)
      .then(r => setItems(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dias]);

  const porEstado = items.reduce((acc, it) => {
    acc[it.estado] = (acc[it.estado] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-end gap-3 bg-white border border-[#d1dbd6] rounded-md p-3">
        <div>
          <Label>Rango (días)</Label>
          <div className="flex gap-1">
            {[30, 60, 90, 180].map(d => (
              <Button key={d} variant={dias === d ? "default" : "outline"} className={dias === d ? "bg-[#205c45]" : ""} size="sm" onClick={() => setDias(d)}>{d}d</Button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 ml-auto">
          <div className="text-xs text-[#5c7368] pt-2">
            {porEstado.vencido ? <Badge className="mr-1 bg-[#fdecea] text-[#7a2514] border border-[#d99584]">Vencidos: {porEstado.vencido}</Badge> : null}
            {porEstado.critico ? <Badge className="mr-1 bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]">≤30d: {porEstado.critico}</Badge> : null}
            {porEstado.proximo ? <Badge className="mr-1 bg-[#e9f2ee] text-[#205c45] border border-[#9dd4ae]">≤90d: {porEstado.proximo}</Badge> : null}
          </div>
          <Button onClick={() => downloadPDF(`/vendor-cartera/mi-inventario-caducar/pdf?dias=${dias}`, `inventario-caducar-${new Date().toISOString().slice(0,10)}.pdf`)} className="bg-[#205c45]" data-testid="inv-pdf-btn">
            <FileDown size={14} className="mr-1" />Descargar PDF
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#d1dbd6] rounded-md overflow-hidden">
        <table className="erp-table text-xs" data-testid="tabla-inventario-caducar">
          <thead><tr><th>Código</th><th>Producto</th><th>Lote</th><th>Vencimiento</th><th className="text-center">Días</th><th className="text-right">Stock</th><th>Estado</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-6 text-[#8ba397]">Cargando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-6 text-[#1f6b42]">Sin lotes por caducar en ≤{dias} días ✓</td></tr>
            ) : items.map((it, i) => {
              const color = it.estado === "vencido" || it.estado === "critico" ? "text-[#7a2514]" : it.estado === "proximo" ? "text-[#8a5500]" : "text-[#1f6b42]";
              const bg = it.estado === "vencido" ? "bg-[#fdecea]" : it.estado === "critico" ? "bg-[#fef7ec]" : it.estado === "proximo" ? "bg-[#e9f2ee]" : "";
              return (
                <tr key={i} className={bg}>
                  <td className="font-mono">{it.codigo_interno || "—"}</td>
                  <td className="font-medium">{it.producto_nombre}</td>
                  <td className="font-mono">{it.numero_lote}</td>
                  <td>{it.fecha_vencimiento}</td>
                  <td className={`text-center font-semibold tabular-nums ${color}`}>{it.dias_restantes}</td>
                  <td className="text-right tabular-nums font-semibold">{it.cantidad_disponible}</td>
                  <td><Badge variant="outline" className={`capitalize text-[10px] ${color}`}>{it.estado}</Badge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}



function CompromisosTab() {
  const [items, setItems] = useState([]);
  const [efectividad, setEfectividad] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [filterEstado, setFilterEstado] = useState("todos");

  function empty() { return { cliente_id: "", fecha_prometida: "", monto: 0, monto_cobrado: 0, metodo_pago: "transferencia", notas: "", factura_venta_id: "" }; }

  const load = () => {
    api.get("/vendor-cartera/compromisos").then(r => setItems(r.data)).catch(e => toast.error(formatApiError(e)));
    api.get("/vendor-cartera/compromisos/efectividad").then(r => setEfectividad(r.data)).catch(() => {});
    api.get("/masters/clientes").then(r => setClientes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.cliente_id || !form.fecha_prometida || !form.monto) {
      return toast.error("Completa cliente, fecha y monto");
    }
    try {
      if (editing) {
        await api.put(`/vendor-cartera/compromisos/${editing.id}`, form);
        toast.success("Compromiso actualizado");
      } else {
        await api.post("/vendor-cartera/compromisos", form);
        toast.success("Compromiso creado");
      }
      setDialogOpen(false); setEditing(null); setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const eliminar = async (c) => {
    if (!window.confirm(`¿Eliminar compromiso de ${c.cliente_nombre} ($${c.monto})?`)) return;
    try {
      await api.delete(`/vendor-cartera/compromisos/${c.id}`);
      toast.success("Compromiso eliminado"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const editar = (c) => {
    setEditing(c);
    setForm({
      cliente_id: c.cliente_id,
      fecha_prometida: c.fecha_prometida,
      monto: c.monto,
      monto_cobrado: c.monto_cobrado || 0,
      metodo_pago: c.metodo_pago || "transferencia",
      notas: c.notas || "",
      factura_venta_id: c.factura_venta_id || "",
    });
    setDialogOpen(true);
  };

  const filtered = items.filter(c => filterEstado === "todos" || c.estado === filterEstado);
  const styleEst = {
    pendiente: "bg-[#fef7ec] text-[#8a5500] border-[#f5c477]",
    cumplido: "bg-[#def5e6] text-[#1f6b42] border-[#9dd4ae]",
    parcial: "bg-[#fef7ec] text-[#8a5500] border-[#f5c477]",
    incumplido: "bg-[#fdecea] text-[#7a2514] border-[#d99584]",
  };

  return (
    <div className="mt-4 space-y-4">
      {efectividad && (
        <div className="grid md:grid-cols-5 gap-3">
          <KPI label="Total" value={efectividad.total_compromisos} />
          <KPI label="Cumplidos" value={efectividad.cumplidos} color="#1f6b42" />
          <KPI label="Pendientes" value={efectividad.pendientes} color="#8a5500" />
          <KPI label="Incumplidos" value={efectividad.incumplidos} color="#7a2514" />
          <KPI label="Efectividad" value={efectividad.efectividad_pct != null ? `${efectividad.efectividad_pct}%` : "—"} color="#205c45" />
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-[#d1dbd6] rounded-md p-3">
        <div className="flex gap-1">
          {["todos", "pendiente", "cumplido", "parcial", "incumplido"].map(s => (
            <Button key={s} size="sm" variant={filterEstado === s ? "default" : "outline"} className={filterEstado === s ? "bg-[#205c45] capitalize" : "capitalize"} onClick={() => setFilterEstado(s)} data-testid={`filter-${s}`}>{s}</Button>
          ))}
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty()); setDialogOpen(true); }} className="bg-[#205c45]" data-testid="new-compromiso-btn">
          <Plus size={14} className="mr-1" />Nuevo compromiso
        </Button>
      </div>

      <div className="bg-white border border-[#d1dbd6] rounded-md overflow-hidden">
        <table className="erp-table text-xs">
          <thead><tr><th>Cliente</th><th>Fecha prometida</th><th className="text-right">Monto</th><th className="text-right">Cobrado</th><th>Método</th><th>Notas</th><th>Estado</th><th className="w-28"></th></tr></thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} data-testid={`row-comp-${c.id}`}>
                <td className="font-medium">{c.cliente_nombre}</td>
                <td>{c.fecha_prometida}</td>
                <td className="text-right tabular-nums font-semibold">${(c.monto || 0).toLocaleString("es-EC", { minimumFractionDigits: 2 })}</td>
                <td className="text-right tabular-nums text-[#1f6b42]">${(c.monto_cobrado || 0).toLocaleString("es-EC", { minimumFractionDigits: 2 })}</td>
                <td className="capitalize">{c.metodo_pago || "—"}</td>
                <td className="max-w-[260px] truncate" title={c.notas}>{c.notas || "—"}</td>
                <td><Badge variant="outline" className={`capitalize text-[10px] ${styleEst[c.estado] || ""}`}>{c.estado}</Badge></td>
                <td>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => editar(c)} data-testid={`edit-comp-${c.id}`}><Edit3 size={12} /></Button>
                    <Button size="sm" variant="outline" className="text-[#b53a25]" onClick={() => eliminar(c)} data-testid={`del-comp-${c.id}`}><XCircle size={12} /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-[#8ba397]">Sin compromisos {filterEstado !== "todos" ? `en estado "${filterEstado}"` : ""}</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="compromiso-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar compromiso" : "Nuevo compromiso de pago"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Cliente</Label>
              <select value={form.cliente_id} onChange={(e) => setForm({ ...form, cliente_id: e.target.value })} className="w-full h-10 border border-[#d1dbd6] rounded px-3 text-sm bg-white" data-testid="comp-cliente" disabled={!!editing}>
                <option value="">— Seleccionar —</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.razon_social}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fecha prometida</Label><Input type="date" value={form.fecha_prometida} onChange={(e) => setForm({ ...form, fecha_prometida: e.target.value })} data-testid="comp-fecha" /></div>
              <div><Label>Monto comprometido</Label><Input type="number" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: parseFloat(e.target.value) || 0 })} data-testid="comp-monto" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Monto cobrado (si aplica)</Label><Input type="number" step="0.01" value={form.monto_cobrado} onChange={(e) => setForm({ ...form, monto_cobrado: parseFloat(e.target.value) || 0 })} data-testid="comp-cobrado" /></div>
              <div>
                <Label>Método de pago</Label>
                <select value={form.metodo_pago} onChange={(e) => setForm({ ...form, metodo_pago: e.target.value })} className="w-full h-10 border border-[#d1dbd6] rounded px-3 text-sm bg-white">
                  <option value="transferencia">Transferencia</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="cheque">Cheque</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>
            <div><Label>Notas</Label><Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} rows={2} placeholder="Ej: Cliente confirmó pago para esa fecha por WhatsApp" data-testid="comp-notas" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45]" data-testid="save-compromiso-btn"><CheckCircle2 size={14} className="mr-1" />{editing ? "Guardar cambios" : "Crear compromiso"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, color = "#15241d" }) {
  return (
    <div className="rounded-md border border-[#d1dbd6] bg-white p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#5c7368]">{label}</div>
      <div className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}
